<script setup lang="ts">
/**
 * Serves as a generalized component for providing the project context to routes or regular components
 *
 * @example
 * <ProjectContextProvider :project-id="projectId"><ComponentThatExectsProjectContext /></ProjectContextProvider>
 *
 * @example
 * const parentRoute = {
 *  name: 'ParentRoute',
 *  path: '/parent',
 *  components: {
 *    default: ProjectContextProvider,
 *    BreadCrumbs: ProjectContextProvider,
 *  },
 *  props: {
 *    default: (route) => ({ projectId: route.params.projectId, routerViewName: 'default' }),
 *    BreadCrumbs: (route) => ({ projectId: route.params.projectId, routerViewName: 'BreadCrumbs' }),
 *  },
 *  children: [
 *    ...
 *  ],
 * }
 */
import { useWorkspaceContext } from '@/modules/Workspaces/useWorkspaceContext'
import { toRef } from 'vue'
import { RouterView } from 'vue-router'
import { provideProjectContext } from './useProjectContext'

const props = defineProps<{ projectId: string; routerViewName?: string }>()

const { workspaceId } = useWorkspaceContext()
provideProjectContext({ projectId: toRef(props, 'projectId'), workspaceId })
</script>

<template>
  <RouterView
    v-if="routerViewName"
    :name="routerViewName"
  />
  <slot v-else />
</template>
